import { flexRender } from "@tanstack/react-table";
import {
  AppTable,
  AppTableBody,
  AppTableCell,
  AppTableContainer,
  AppTableHead,
  AppTableRow,
  AppTableSort,
} from "../../../../components/common/app-table/ui";
import useDefaultTable from "../../../../components/common/app-table/useDefaultTable";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import TablePagination from "@mui/material/TablePagination";
import LoadingIndicator from "../../../../components/common/LoadingIndicator";
import AppSearchField from "../../../../components/common/forms/AppSearchField";
import { ExportButton } from "../../../../components/common/primebmd-ui/DataButtons";
import {
  useGetAllESR,
  useFormatESRData,
} from "../../../../hooks/useESRApproval";
import { useSnackBar } from "../../../../components/ToastProvider";
import { exportESRToExcel } from "../utils/exporting";
import { ESRColumns } from "../shared/ESR/utils/types";
import { useCreateESRWIPColumns } from "../shared/ESR/hooks/useCreateESRWipColumns";
import { useAuth } from "../../../../components/AuthProvider";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";

const ESRWipTable = () => {
  const currUser = useAuth();
  const location = useLocation();
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const { showSnackBar } = useSnackBar();
  const esrData = useGetAllESR("Approved", (pathSegments[0] == "esr-management" ? currUser?.authUser?.id || "undefined" : ""));
  const data = esrData?.data === undefined ? [] : esrData.data;
  const formattedESRData: ESRColumns[] = useFormatESRData(data, "Approved");
  const esrColumns = useCreateESRWIPColumns();

  useEffect(() => {
    if (currUser?.authUser?.id) {
      esrData?.refetch();
    }
  }, [currUser]);

  const {
    table,
    dataCount,
    searchFilter,
    setSearchFilter,
    pageIndex,
    pageSize,
  } = useDefaultTable(formattedESRData, esrColumns);

  const emptyText = searchFilter ? "No data found" : "No data yet";

  if (esrData?.isLoading) return <LoadingIndicator show />;

  const handleExport = () => {
    const selectedRows = table.getSelectedRowModel().rows;

    const hasSelectedRows = selectedRows.length > 0;

    const dataToExport = hasSelectedRows
      ? selectedRows.map((row) => {
          const { id, ...rest } = row.original; // Destructure to exclude `id`
          return rest; // Return the new object without `id`
        })
      : formattedESRData.map((item) => {
          const { id, ...rest } = item; // Destructure to exclude `id`
          return rest; // Return the new object without `id`
        });

    exportESRToExcel(dataToExport, "JO_WIP");
  };

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" mb={2} gap={2}>
        <AppSearchField
          onSearchKeywordChange={setSearchFilter}
          searchKeyword={searchFilter ?? ""}
        />
        <ExportButton onClick={handleExport} />
      </Stack>
      <Paper elevation={3}>
        <AppTableContainer>
          <AppTable stickyHeader>
            <AppTableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <AppTableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <AppTableCell key={header.id} align="center">
                      <AppTableSort
                        canSort={header.column.getCanSort()}
                        currentSort={header.column.getIsSorted()}
                        onSort={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </AppTableSort>
                    </AppTableCell>
                  ))}
                </AppTableRow>
              ))}
            </AppTableHead>
            <AppTableBody
              dataCount={dataCount}
              loading={esrData?.isLoading}
              emptyText={emptyText}
            >
              {table.getRowModel().rows.map((row) => (
                <AppTableRow key={row.id} sx={{ height: "45px" }}>
                  {row.getVisibleCells().map((cell) => (
                    <AppTableCell key={cell.id} align="center">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </AppTableCell>
                  ))}
                </AppTableRow>
              ))}
            </AppTableBody>
          </AppTable>
        </AppTableContainer>
        <Box position="relative">
          <TablePagination
            sx={{ bgcolor: "white", borderTop: "1px solid #eee" }}
            component="div"
            count={dataCount}
            page={pageIndex}
            onPageChange={(e, page) => table.setPageIndex(page)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) =>
              table.setPageSize(Number(e.target.value))
            }
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Box>
      </Paper>
      {esrData?.isError && showSnackBar("Network Error", "error")}
    </>
  );
};

export default ESRWipTable;
